/**
 * TypewriterText — Animates text appearing character by character.
 */

import { useState, useEffect, useRef } from 'react'

interface TypewriterTextProps {
  text: string
  speed?: number
  className?: string
  onComplete?: () => void
}

export function TypewriterText({
  text,
  speed = 20,
  className = '',
  onComplete,
}: TypewriterTextProps) {
  const [displayed, setDisplayed] = useState('')
  const indexRef = useRef(0)
  const prevTextRef = useRef(text)

  useEffect(() => {
    // Reset when text changes
    if (text !== prevTextRef.current) {
      setDisplayed('')
      indexRef.current = 0
      prevTextRef.current = text
    }

    if (indexRef.current >= text.length) {
      onComplete?.()
      return
    }

    const timer = setInterval(() => {
      indexRef.current += 1
      setDisplayed(text.slice(0, indexRef.current))
      if (indexRef.current >= text.length) {
        clearInterval(timer)
        onComplete?.()
      }
    }, speed)

    return () => clearInterval(timer)
  }, [text, speed, onComplete])

  return (
    <span className={className}>
      {displayed}
      {displayed.length < text.length && (
        <span className="animate-pulse text-text-muted">|</span>
      )}
    </span>
  )
}
